<template>
  <div>
    <div class="page-header">
      <div class="add-item d-flex">
        <div class="page-title">
          <h4>{{ $t("contra_voucher") }}</h4>
        </div>
      </div>

      <div class="page-btn">
        <router-link :to="{ name: 'ContraVoucherCreate' }" class="btn btn-added">
          <i class="ti ti-circle-plus me-2 text-white"></i>{{ $t("add_contra_voucher") }}
        </router-link>
      </div>
    </div>

    <ThemeDataTable
      ref="contraTable"
      table-id="ContraVoucherTable"
      :fetch-data="fetchVouchersPage"
      :filters="tableFilters"
      :default-order-column="1"
      default-order-dir="desc"
    >
      <template #default="{ rows }">
        <thead class="thead-light">
          <tr>
            <th>{{ $t("voucher_no") }}</th>
            <th>{{ $t("voucher_date") }}</th>
            <th>{{ $t("head") }}</th>
            <th>{{ $t("narration") }}</th>
            <th>{{ $t("debit") }}</th>
            <th>{{ $t("credit") }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in rows" :key="row.id">
            <td>{{ row.voucher_id }}</td>
            <td>{{ row.voucher_date }}</td>
            <td>
              <div class="d-flex align-items-center">
                <span class="avatar avatar-md me-2">
                  <span
                    class="avatar-title rounded bg-light text-secondary d-flex align-items-center justify-content-center w-100 h-100"
                  >
                    <i class="ti ti-building-bank"></i>
                  </span>
                </span>
                <div>
                  <div class="text-uppercase">{{ row.head_name || '—' }}</div>
                  <small class="text-muted">{{ row.account_id }}</small>
                </div>
              </div>
            </td>
            <td>{{ row.narration || '—' }}</td>
            <td>{{ formatMoney(row.debit) }}</td>
            <td>{{ formatMoney(row.credit) }}</td>
          </tr>
        </tbody>
      </template>
    </ThemeDataTable>
  </div>
</template>

<script>
import apiUrls from '@/urls';
import ThemeDataTable from '@/components/common/ThemeDataTable.vue';
import { truncateMoney2 } from '@/utils';

// Vouchers entered on the voucher pages are numbered 15YYYYMMDDNNN.
const VOUCHER_PREFIX = '15';

// thead index -> how that column sorts
const SORT_KEYS = {
  0: (row) => row.voucher_id || '',
  1: (row) => row.voucher_date || '',
  2: (row) => row.head_name || '',
  3: (row) => row.narration || '',
  4: (row) => Number(row.debit) || 0,
  5: (row) => Number(row.credit) || 0,
};

export default {
  components: {
    ThemeDataTable,
  },
  data() {
    return {
      // The endpoint returns every contra row at once, so paging, search and sort happen here.
      ledgerRows: null,
      loadingRows: null,
    };
  },
  computed: {
    headOptions() {
      return [...new Set((this.ledgerRows || []).map((row) => row.head_name).filter(Boolean))];
    },

    tableFilters() {
      return [
        {
          key: 'head',
          label: this.$t('head'),
          type: 'select2',
          options: this.headOptions,
        },
      ];
    },
  },
  methods: {
    formatMoney(amount) {
      return truncateMoney2(amount);
    },

    loadRows() {
      if (!this.loadingRows) {
        this.loadingRows = Promise.all([
          this.$axios.get(apiUrls.journal_voucher.get, {
            params: { flag: 'CT', transaction_status: 'AV' },
          }),
          this.$axios.get(apiUrls.chart_of_accounts.get, { params: { status: 'Child' } }),
        ])
          .then(([ledgerRes, headsRes]) => {
            const headNames = new Map(
              (headsRes.data || []).map((head) => [String(head.account_id), (head.node || '').trim()])
            );
            this.ledgerRows = (ledgerRes.data || [])
              .filter((row) => String(row.voucher_id || '').startsWith(VOUCHER_PREFIX))
              .map((row) => ({
              ...row,
              head_name: headNames.get(String(row.account_id)) || '',
            }));
          })
          .catch((error) => {
            this.loadingRows = null;
            throw error;
          });
      }
      return this.loadingRows;
    },

    async fetchVouchersPage({ start, length, search, orderColumn, orderDir, filters }) {
      await this.loadRows();
      const all = this.ledgerRows;
      let rows = all;

      if (filters.head) {
        rows = rows.filter((row) => row.head_name === filters.head);
      }

      const term = (search || '').toLowerCase();
      if (term) {
        rows = rows.filter((row) =>
          [row.voucher_id, row.voucher_date, row.head_name, row.account_id, row.narration]
            .some((value) => String(value ?? '').toLowerCase().includes(term))
        );
      }

      const key = SORT_KEYS[orderColumn] || SORT_KEYS[1];
      const dir = orderDir === 'desc' ? -1 : 1;
      rows = [...rows].sort((a, b) => {
        const x = key(a);
        const y = key(b);
        const cmp = typeof x === 'number' ? x - y : String(x).localeCompare(String(y));
        return cmp * dir || (b.id - a.id);
      });

      return {
        data: rows.slice(start, start + length),
        recordsTotal: all.length,
        recordsFiltered: rows.length,
      };
    },
  },
};
</script>
